import React from 'react';
import { Form, Field } from 'react-final-form';

// Types
import { GoalItem } from 'Shared/constants/types';
import { ReminderType } from 'Dashboard/components/CreateOrUpdateGoalPageV3/types';

// Constants
import { reminderTypeOptions } from 'Dashboard/components/CreateOrUpdateGoalPageV3/constants';

// Utils
import { formatOptionsFromArray } from 'Dashboard/components/CreateOrUpdateGoalPageV3/PrivacyAndReminders/utils';
import { getRecurrenceAttributes } from 'Dashboard/components/CreateOrUpdateGoalPageV3/utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useGoal } from 'Shared/utils/hooks/use-goal';

// Components
import { WpModal, WpModalHeader, WpModalBody } from 'Wpds/components/modal';
import GoalName from '../GoalName/GoalName';
import SettingsContainer from 'Shared/components/SettingsContainer/SettingsContainer';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import FormRadioGroup from 'Shared/components/FormComponents/FormRadioGroup';
import FieldMetricType from 'Dashboard/components/CreateOrUpdateGoalPageV3/GoalTracking/FieldMetricType';

// Styles
import './GoalSettingsModal.scss';

export enum SettingsType {
  progress = 'progress',
  privacy = 'privacy',
  notifications = 'notifications',
}

type Props = {
  goal: GoalItem;
  isOpen: boolean;
  toggleIsOpen: () => void;
  settingsToDisplay?: Array<SettingsType>;
};

const GoalSettingsModal = ({
  goal,
  isOpen,
  toggleIsOpen,
  settingsToDisplay = [SettingsType.progress, SettingsType.privacy, SettingsType.notifications],
}: Props) => {
  const { tr } = useTranslation();
  const { handleUpdateGoal, workspaceLabel } = useGoal({ slug: goal.slug });

  const [isSavingMetricType, toggleIsSavingMetricType] = useToggle(false);
  const [isSavingReminder, toggleIsSavingReminder] = useToggle(false);
  const [isSavingPrivacy, toggleIsSavingPrivacy] = useToggle(false);

  const showProgressSetting = settingsToDisplay.includes(SettingsType.progress);
  const showPrivacySetting = settingsToDisplay.includes(SettingsType.privacy);
  const showNotificationSetting = settingsToDisplay.includes(SettingsType.notifications);

  const reminderOptions = formatOptionsFromArray(reminderTypeOptions);

  const handleUpdateMetricType = values => {
    toggleIsSavingMetricType();
    handleUpdateGoal({ ...values }).then(() => {
      toggleIsSavingMetricType();
    });
  };
  const handleUpdatePrivacy = values => {
    toggleIsSavingPrivacy();
    handleUpdateGoal({ private: values.privacy === 'private' }).then(() => {
      toggleIsSavingPrivacy();
    });
  };

  const handleUpdateReminder = values => {
    toggleIsSavingReminder();
    handleUpdateGoal({ recurrenceAttributes: getRecurrenceAttributes(values) }).then(() => {
      toggleIsSavingReminder();
    });
  };

  const baseClassName = 'GoalSettingsModal';

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} size="sm">
      <WpModalHeader>{tr('perform.goals.settings.modal_title')}</WpModalHeader>
      <WpModalBody>
        <GoalName goal={goal} showLinkedIndicator={false} />
        {showProgressSetting && (
          <SettingsContainer title={tr('perform.goals.settings.measure_progress_question')}>
            <Form
              initialValues={{
                metricType: goal.metricType,
                metricInitialValue: goal.metricInitialValue,
                metricCurrentValue: goal.metricCurrentValue,
                metricGoalValue: goal.metricGoalValue,
              }}
              onSubmit={handleUpdateMetricType}
            >
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Field name="metricType" component={FieldMetricType} inSetting />
                  <div className={`${baseClassName}__buttons`}>
                    <WpButtonGroup spacing="md">
                      <WpButton size="xs" square slimPadding btnType="submit" loading={isSavingMetricType}>
                        {tr('perform.goals.profile.save_button')}
                      </WpButton>
                    </WpButtonGroup>
                  </div>
                </form>
              )}
            </Form>
          </SettingsContainer>
        )}
        {showPrivacySetting && (
          <SettingsContainer title={tr('perform.goals.settings.who_can_view_question')}>
            <Form initialValues={{ privacy: goal.private ? 'private' : 'public' }} onSubmit={handleUpdatePrivacy}>
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Field
                    name="privacy"
                    options={[
                      { label: tr('perform.goals.settings.everyone_in_organization'), value: 'public' },
                      { label: tr('perform.goals.settings.just_workspace_members', workspaceLabel), value: 'private' },
                    ]}
                    component={FormRadioGroup}
                  />
                  <div className={`${baseClassName}__buttons`}>
                    <WpButtonGroup spacing="md">
                      <WpButton size="xs" square slimPadding btnType="submit" loading={isSavingPrivacy}>
                        {tr('perform.goals.profile.save_button')}
                      </WpButton>
                    </WpButtonGroup>
                  </div>
                </form>
              )}
            </Form>
          </SettingsContainer>
        )}
        {showNotificationSetting && (
          <SettingsContainer
            title={tr('perform.goals.settings.checkin_frequency_question')}
            subTitle={tr('perform.goals.settings.checkin_notification_description', workspaceLabel)}
          >
            <Form initialValues={{ reminder: ReminderType[goal?.reminderSetting] }} onSubmit={handleUpdateReminder}>
              {({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                  <Field name="reminder" options={reminderOptions} component={FormRadioGroup} />
                  <div className={`${baseClassName}__buttons`}>
                    <WpButtonGroup spacing="md">
                      <WpButton
                        qaTest="goal-settings-modal-checkin-save-button"
                        size="xs"
                        square
                        slimPadding
                        btnType="submit"
                        loading={isSavingReminder}
                      >
                        {tr('perform.goals.profile.save_button')}
                      </WpButton>
                    </WpButtonGroup>
                  </div>
                </form>
              )}
            </Form>
          </SettingsContainer>
        )}
      </WpModalBody>
    </WpModal>
  );
};

export default GoalSettingsModal;
